<template>
    <tailwind-layout title="Campaign Email Subjects">
        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">Campaign Email Subjects</h1>
                <p class="mt-1 text-sm text-gray-600">
                    Every marketing email RentRisk has captured a click from, by its {{ 'utm_content' }}. Enter the subject
                    line each one was sent with and Campaign Attribution shows it in place of the slug. Clear a subject to
                    unmap it.
                </p>
                <p class="mt-2 text-sm font-medium text-gray-900">
                    {{ unmappedCount }} of {{ rows.length }} not mapped yet
                </p>
            </header>

            <div class="rounded-md shadow-none overflow-x-auto lg:shadow">
                <div class="inline-block min-w-full align-middle">
                    <div class="overflow-hidden shadow lg:border">
                        <table class="min-w-full divide-y divide-gray-300">
                            <thead class="bg-blue-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">utm_content</th>
                                    <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">Clicks</th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Last Click</th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Email Subject</th>
                                    <th scope="col" class="px-6 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in rows" :key="row.utm_content" class="bg-white align-top hover:bg-gray-100">
                                    <td class="break-all px-6 py-4 text-sm text-gray-500">{{ row.utm_content }}</td>
                                    <td class="px-6 py-4 text-center text-sm text-gray-500">{{ row.clicks }}</td>
                                    <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{{ (row.last_clicked_at || '').slice(0, 10) }}</td>
                                    <td class="w-1/2 px-6 py-4">
                                        <input
                                            v-model="drafts[row.utm_content]"
                                            type="text"
                                            maxlength="255"
                                            placeholder="Not mapped yet"
                                            :aria-label="`Email subject for ${row.utm_content}`"
                                            class="block w-full rounded-md border-gray-100 py-2 text-sm shadow-sm drop-shadow focus:border-rent-risk-blue-400 focus:outline-none focus:ring-rent-risk-blue-400"
                                            @keydown.enter.prevent="save(row)"
                                        >
                                        <p v-if="errorFor(row)" class="mt-1 text-xs text-red-600">{{ errorFor(row) }}</p>
                                    </td>
                                    <td class="whitespace-nowrap px-6 py-4 text-right text-sm">
                                        <span v-if="saved[row.utm_content]" class="mr-3 text-green-600">Saved</span>
                                        <button
                                            type="button"
                                            class="rounded-md bg-rent-risk-blue-400 px-3 py-2 font-medium text-white disabled:opacity-40"
                                            :disabled="saving[row.utm_content] || !isDirty(row)"
                                            @click="save(row)"
                                        >
                                            {{ saving[row.utm_content] ? 'Saving…' : 'Save' }}
                                        </button>
                                    </td>
                                </tr>
                                <tr v-show="!rows.length">
                                    <td colspan="5" class="px-6 py-4 text-center text-sm font-medium text-dark-gray-400">
                                        No campaign clicks captured yet.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { router } from '@inertiajs/vue3'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import BridgeBackLink from '@/Shared/BridgeBackLink.vue'

const props = defineProps({
    rows: { type: Array, default: () => [] },
})

const drafts = ref(Object.fromEntries(props.rows.map((row) => [row.utm_content, row.subject || ''])))
const saving = ref({})
const saved = ref({})
const errors = ref({})

const unmappedCount = computed(() => props.rows.filter((row) => !row.subject).length)

const isDirty = (row) => (drafts.value[row.utm_content] || '').trim() !== (row.subject || '')

const errorFor = (row) => {
    const rowErrors = errors.value[row.utm_content] || {}

    return rowErrors.subject || rowErrors.utm_content || null
}

const save = (row) => {
    const key = row.utm_content

    if (saving.value[key] || !isDirty(row)) {
        return
    }

    saving.value[key] = true
    errors.value[key] = {}
    saved.value[key] = false

    router.put(route('bridge.campaign-attribution.subjects.update'), {
        utm_content: key,
        subject: drafts.value[key],
    }, {
        preserveScroll: true,
        preserveState: true,
        onError: (rowErrors) => { errors.value[key] = rowErrors },
        onSuccess: () => {
            saved.value[key] = true
            setTimeout(() => { saved.value[key] = false }, 2500)
        },
        onFinish: () => { saving.value[key] = false },
    })
}
</script>
